/**
 * EmailDocumentationPage Component
 * AIDLC Administrative Backend
 *
 * Comprehensive feature and user documentation for the Email Management
 * module. Covers inbox usage, signature configuration, agent-assisted
 * reply drafting, and OneDrive file browsing.
 *
 * Structure:
 * - Overview with feature summary
 * - Inbox Guide (reading, replying, agent drafts)
 * - Signature Guide (template fields, preview, branding)
 * - OneDrive Guide (browsing, downloading)
 * - Azure Permissions reference
 * - Troubleshooting
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

// ─── Types ────────────────────────────────────────────────────────

interface Section {
  id: string;
  title: string;
  icon: React.ReactNode;
}

// ─── Constants ────────────────────────────────────────────────────

const SECTIONS: Section[] = [
  {
    id: 'overview',
    title: 'Overview',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
    ),
  },
  {
    id: 'inbox',
    title: 'Inbox',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M2.25 13.5h3.86a2.25 2.25 0 012.012 1.244l.256.512a2.25 2.25 0 002.013 1.244h3.218a2.25 2.25 0 002.013-1.244l.256-.512a2.25 2.25 0 012.013-1.244h3.859m-19.5.338V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18v-4.162c0-.224-.034-.447-.1-.661L19.24 5.338a2.25 2.25 0 00-2.15-1.588H6.911a2.25 2.25 0 00-2.15 1.588L2.35 13.177a2.25 2.25 0 00-.1.661z" />
      </svg>
    ),
  },
  {
    id: 'signature',
    title: 'Signature',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
      </svg>
    ),
  },
  {
    id: 'onedrive',
    title: 'OneDrive',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
      </svg>
    ),
  },
  {
    id: 'permissions',
    title: 'Azure Setup',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path d="M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l5.653-4.655m0 0a3.75 3.75 0 115.304-5.304m-5.304 5.304l5.304-5.304" />
      </svg>
    ),
  },
];

// ─── Section Content Components ───────────────────────────────────

function OverviewSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Email Management module integrates Microsoft 365 mailbox access directly into
        the AIDLC Admin Portal. It provides three core capabilities accessible from a single
        page at <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">/email</code>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          {
            title: 'Inbox',
            description: 'Read, reply to, and manage emails from configured mailboxes. Supports agent-assisted reply drafting.',
            badge: 'Read & Reply',
          },
          {
            title: 'Signature',
            description: 'Configure the LDAJA-branded email signature template with custom valediction, title, and tagline.',
            badge: 'Branding',
          },
          {
            title: 'OneDrive',
            description: 'Browse and download files from OneDrive for attaching quotes, contracts, and NDAs to replies.',
            badge: 'Files',
          },
        ].map((feature) => (
          <div key={feature.title} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-sm font-semibold text-ldaja-black">{feature.title}</h4>
              <Badge variant="info">{feature.badge}</Badge>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{feature.description}</p>
          </div>
        ))}
      </div>

      <Card title="Access Requirements" subtitle="Who can use this feature">
        <div className="space-y-2 text-xs text-gray-600">
          <div className="flex items-center gap-3">
            <Badge variant="info">Role</Badge>
            <span>Admin or Operator role required for all inbox and OneDrive operations</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="purple">Persona</Badge>
            <span>Visible under the <strong>Ops</strong> persona in the sidebar navigation</span>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="warning">Backend</Badge>
            <span>Requires <code className="bg-gray-100 px-1 rounded">GRAPH_CLIENT_SECRET</code> and <code className="bg-gray-100 px-1 rounded">GRAPH_SENDER_EMAIL</code> environment variables</span>
          </div>
        </div>
      </Card>
    </div>
  );
}

function InboxSection() {
  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-ldaja-black">Reading Messages</h3>
      <div className="space-y-3">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <ol className="space-y-3 text-xs text-gray-600">
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-ldaja-blue">1</span>
              </span>
              <div>
                <strong className="text-gray-700">Select a mailbox</strong> from the dropdown at the top of the page. The default mailbox is <code className="bg-gray-100 px-1 rounded">dan@ldaja.com</code>.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-ldaja-blue">2</span>
              </span>
              <div>
                <strong className="text-gray-700">Click a message</strong> in the list to view its full body. Unread messages are indicated with a blue dot and bold text.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-ldaja-blue">3</span>
              </span>
              <div>
                <strong className="text-gray-700">Messages are automatically marked as read</strong> when you open them in the detail pane.
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-ldaja-blue">4</span>
              </span>
              <div>
                <strong className="text-gray-700">Paginate</strong> through messages using the Previous / Next buttons at the bottom. 25 messages are shown per page.
              </div>
            </li>
          </ol>
        </div>
      </div>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Replying to Messages</h3>
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
        <ol className="space-y-3 text-xs text-gray-600">
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">1</span>
            </span>
            <div>
              <strong className="text-gray-700">Click "Reply"</strong> on a selected message to open the reply composer.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">2</span>
            </span>
            <div>
              <strong className="text-gray-700">Type your reply</strong> in the text area. The reply will be sent as plain text (HTML-encoded on the server for safety).
            </div>
          </li>
          <li className="flex gap-3">
            <span className="w-5 h-5 rounded-full bg-ldaja-blue/10 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-ldaja-blue">3</span>
            </span>
            <div>
              <strong className="text-gray-700">Click "Send Reply"</strong> to send. Your LDAJA signature is automatically appended to every outgoing reply.
            </div>
          </li>
        </ol>
      </div>

      <h3 className="text-sm font-semibold text-ldaja-black mt-6">Agent-Assisted Drafts</h3>
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <p className="text-xs text-blue-800 leading-relaxed mb-3">
          You can ask an AIDLC agent to draft a reply based on the email&apos;s context. The agent will
          generate a professional response written from Dan&apos;s perspective, including a unique valediction.
        </p>
        <ol className="space-y-2 text-xs text-blue-700">
          <li className="flex gap-2">
            <span className="font-bold">1.</span>
            Click <strong>"Draft with Agent"</strong> on a selected message.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">2.</span>
            Select an agent from the dropdown (e.g., Dan, Marcus, Maya).
          </li>
          <li className="flex gap-2">
            <span className="font-bold">3.</span>
            The system constructs a prompt with the email context and returns it for the agent.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">4.</span>
            Use the Chat Interface to send the prompt to the selected agent for a full draft.
          </li>
          <li className="flex gap-2">
            <span className="font-bold">5.</span>
            Review and edit the draft before sending.
          </li>
        </ol>
      </div>
    </div>
  );
}

function SignatureSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Signature tab lets you configure the LDAJA-branded email signature that is
        automatically appended to every outgoing reply. The signature follows the brand
        guidelines with the LDAJA logo and &quot;BUILD SOMETHING BETTER&quot; tagline.
      </p>

      <Card title="Signature Fields" subtitle="What each field controls">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Field</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Description</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Default</th>
              </tr>
            </thead>
            <tbody>
              {[
                { field: 'Valediction', desc: 'The sign-off line above your name (e.g., "Onward and upward,")', default: 'Onward and upward,' },
                { field: 'Signer Name', desc: 'The name displayed in the signature', default: 'Dan' },
                { field: 'Signer Title', desc: 'Job title or role', default: 'CEO' },
                { field: 'Signer Email', desc: 'Email address displayed in the signature', default: 'dan@ldaja.com' },
                { field: 'Tagline', desc: 'Brand tagline below the signature', default: 'BUILD SOMETHING BETTER' },
              ].map((row) => (
                <tr key={row.field} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-700">{row.field}</td>
                  <td className="px-3 py-2 text-gray-500">{row.desc}</td>
                  <td className="px-3 py-2 font-mono text-gray-400">{row.default}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Signature Layout">
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-100 space-y-3">
          <div className="text-xs text-gray-500 italic">Onward and upward,</div>
          <div>
            <div className="text-sm font-bold text-ldaja-black">Dan</div>
            <div className="text-xs text-gray-500">CEO</div>
            <div className="text-xs text-ldaja-blue">dan@ldaja.com</div>
          </div>
          <div className="border-t-2 border-ldaja-blue pt-3">
            <div className="text-[10px] font-bold tracking-[2px] text-ldaja-black">
              BUILD SOMETHING BETTER
            </div>
          </div>
        </div>
        <p className="text-[10px] text-gray-400 mt-3">
          Changes save immediately and are reflected in the live preview. The signature persists across server restarts.
        </p>
      </Card>
    </div>
  );
}

function OneDriveSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The OneDrive tab provides a file browser for accessing documents in the selected
        mailbox user&apos;s OneDrive. This is useful for locating quotes, contracts, NDAs,
        and other files that may need to be referenced or attached.
      </p>

      <Card title="File Browser Features">
        <div className="space-y-3">
          {[
            {
              title: 'Folder Navigation',
              description: 'Click folders to browse into them. A breadcrumb trail at the top shows your current path and allows quick navigation back to any parent folder.',
            },
            {
              title: 'File Downloads',
              description: 'Click the download button on any file to get a temporary download URL. The link opens in a new browser tab.',
            },
            {
              title: 'File Details',
              description: 'Each item shows its name, size (formatted as B/KB/MB/GB), and last modified date.',
            },
            {
              title: 'Root Navigation',
              description: 'Click "Root" in the breadcrumb trail or switch mailboxes to return to the top-level OneDrive folder.',
            },
          ].map((feature) => (
            <div key={feature.title} className="flex gap-3">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h4 className="text-xs font-semibold text-gray-700">{feature.title}</h4>
                <p className="text-xs text-gray-500 mt-0.5">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-amber-800 mb-1">Note: Attachment Support</h4>
        <p className="text-xs text-amber-700 leading-relaxed">
          File attachment to email replies is planned for a future release. Currently, the OneDrive
          tab is for browsing and downloading files only. To attach a file to a reply, download it
          first and send it through your email client directly.
        </p>
      </div>
    </div>
  );
}

function PermissionsSection() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600 leading-relaxed">
        The Email Management feature uses the Microsoft Graph API with client credentials
        (application permissions). The following setup is required in Azure.
      </p>

      <Card title="Required Application Permissions" subtitle="Azure Portal > App Registrations > API Permissions">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Permission</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Type</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Used For</th>
                <th className="text-left px-3 py-2 font-semibold text-gray-700">Status</th>
              </tr>
            </thead>
            <tbody>
              {[
                { perm: 'Mail.Send', type: 'Application', use: 'Sending emails', status: 'Granted' },
                { perm: 'User.Read.All', type: 'Application', use: 'User lookups', status: 'Granted' },
                { perm: 'Mail.Read', type: 'Application', use: 'Reading inbox messages', status: 'Required' },
                { perm: 'Mail.ReadWrite', type: 'Application', use: 'Marking messages as read', status: 'Required' },
                { perm: 'Files.Read.All', type: 'Application', use: 'Browsing OneDrive files', status: 'Required' },
              ].map((row) => (
                <tr key={row.perm} className="border-b border-gray-50">
                  <td className="px-3 py-2 font-mono font-medium text-gray-700">{row.perm}</td>
                  <td className="px-3 py-2 text-gray-500">{row.type}</td>
                  <td className="px-3 py-2 text-gray-500">{row.use}</td>
                  <td className="px-3 py-2">
                    <Badge variant={row.status === 'Granted' ? 'success' : 'warning'}>
                      {row.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card title="Environment Variables" subtitle="Required in .env for Graph API access">
        <div className="space-y-2">
          {[
            { key: 'GRAPH_CLIENT_SECRET', desc: 'Client secret value (not the ID) from Azure AD app registration' },
            { key: 'GRAPH_SENDER_EMAIL', desc: 'Default sender email address (e.g., dan@ldaja.com)' },
            { key: 'AZURE_TENANT_ID', desc: 'Azure AD tenant ID' },
            { key: 'AZURE_CLIENT_ID', desc: 'Azure AD app client ID' },
          ].map((env) => (
            <div key={env.key} className="flex items-start gap-3 py-1.5 border-b border-gray-50 last:border-0">
              <code className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded text-ldaja-blue flex-shrink-0">
                {env.key}
              </code>
              <span className="text-xs text-gray-500">{env.desc}</span>
            </div>
          ))}
        </div>
      </Card>

      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h4 className="text-xs font-semibold text-red-800 mb-1">Important: Client Secret vs Secret ID</h4>
        <p className="text-xs text-red-700 leading-relaxed">
          Azure shows two values for each client secret: the <strong>Secret ID</strong> (a GUID label)
          and the <strong>Value</strong> (the actual secret string). You must use the <strong>Value</strong>,
          not the Secret ID. The Value is only visible immediately after creation — if you can no longer
          see it, create a new client secret.
        </p>
      </div>
    </div>
  );
}

function TroubleshootingSection() {
  return (
    <div className="space-y-4">
      <Card title="Common Issues">
        <div className="space-y-4">
          {[
            {
              problem: '503 "Graph email not configured"',
              cause: 'GRAPH_CLIENT_SECRET or GRAPH_SENDER_EMAIL environment variables are not set.',
              fix: 'Add both variables to your .env file and restart the backend server.',
            },
            {
              problem: '500 "Invalid client secret provided" (AADSTS7000215)',
              cause: 'The GRAPH_CLIENT_SECRET contains the Secret ID instead of the Secret Value.',
              fix: 'Go to Azure Portal > App registrations > Certificates & secrets. Copy the Value column (not Secret ID). If the value is hidden, create a new secret.',
            },
            {
              problem: '403 "Insufficient privileges" on inbox or OneDrive',
              cause: 'The Azure AD app is missing required Graph API permissions.',
              fix: 'Add Mail.Read, Mail.ReadWrite, and Files.Read.All application permissions in Azure Portal. Grant admin consent after adding.',
            },
            {
              problem: 'Inbox returns empty list but mailbox has messages',
              cause: 'The Graph API is reading from the Inbox folder only, not all mail folders.',
              fix: 'This is by design. Messages in subfolders, Sent Items, or other folders are not shown. Only the Inbox folder is queried.',
            },
            {
              problem: 'Reply sends but recipient gets garbled text',
              cause: 'The reply body is HTML-encoded on the server for XSS prevention.',
              fix: 'Write replies in plain text — line breaks are converted to HTML breaks automatically. Do not include HTML tags in the reply text area.',
            },
            {
              problem: 'OneDrive shows only first 100 items in a folder',
              cause: 'The Graph API query is limited to 100 items per request.',
              fix: 'This is a current limitation. Folders with more than 100 items will show only the first 100. Pagination for OneDrive folders is planned.',
            },
          ].map((item, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-800">{item.problem}</span>
              </div>
              <div className="p-3 space-y-2">
                <div>
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Cause</span>
                  <p className="text-xs text-gray-600 mt-0.5">{item.cause}</p>
                </div>
                <div>
                  <span className="text-[10px] font-semibold text-green-600 uppercase tracking-wider">Fix</span>
                  <p className="text-xs text-gray-600 mt-0.5">{item.fix}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Section Renderer ─────────────────────────────────────────────

const SECTION_COMPONENTS: Record<string, () => JSX.Element> = {
  overview: OverviewSection,
  inbox: InboxSection,
  signature: SignatureSection,
  onedrive: OneDriveSection,
  permissions: PermissionsSection,
  troubleshooting: TroubleshootingSection,
};

// ─── Main Component ───────────────────────────────────────────────

export function EmailDocumentationPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const navigate = useNavigate();

  const ActiveContent = SECTION_COMPONENTS[activeSection];

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Page Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <button
              onClick={() => navigate('/docs')}
              className="text-xs text-ldaja-blue hover:underline"
            >
              Documentation
            </button>
            <span className="text-xs text-gray-400">/</span>
            <span className="text-xs text-gray-500">Email Management</span>
          </div>
          <h1 className="text-xl font-bold text-ldaja-black">Email Management Guide</h1>
          <p className="text-sm text-gray-500 mt-1">
            Feature documentation and user guide for inbox, signature, and OneDrive integration
          </p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Badge variant="info">Microsoft Graph</Badge>
            <Badge variant="success" dot>dan@ldaja.com</Badge>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-gray-400">Last Updated</p>
          <p className="text-xs font-medium text-gray-600">2026-02-13</p>
          <p className="text-[10px] text-gray-400 mt-1">Authors</p>
          <p className="text-xs text-gray-600">Dan (Ops), Maya (UI), Elana (Docs)</p>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`flex items-center gap-2 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
              activeSection === section.id
                ? 'border-ldaja-blue text-ldaja-blue'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {section.icon}
            {section.title}
          </button>
        ))}
      </div>

      {/* Section Content */}
      <div>
        {ActiveContent && <ActiveContent />}
      </div>

      {/* Footer */}
      <div className="text-center py-4 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          Email Management feature implemented Feb 2026. For issues, use the Chat Interface or contact Dan (Ops).
        </p>
      </div>
    </div>
  );
}
